/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { Fragment, useEffect, useState } from "react";

//@Packages
import {
  Button,
  Modal,
  Checkbox,
  Input,
  Accordion,
  AccordionItem,
  SearchIcon,
  ContentLoader
} from "@forge/common";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

//@Store
import { RootState } from "../../store";

//@utils
import { validateInput } from "../../utils/utility";
import { GatewayStatus, LOCALE_CONST } from "../../utils/constants";
import { ISiteListProps } from "../../models";
import { GatewaySiteProps } from "../../models/devicelist";

//@Services
import { useGetFilterMenuQuery } from "../../services/api";
import { useGetOrgSiteListsQuery } from "../../services/rbm-api";

//@Styles
import "./FilterModal.scss";

const FilterModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedOrg, customerType, customerOrgId, deviceListFilter } = useSelector((state: RootState) => state.global);

  const [searchQuery, setSearchQuery] = useState("");
  const [sitesSearchQuery, setSitesSearchQuery] = useState("");
  const [getFilterMenuData, setGetFilterMenuData] = useState<any>([]);
  const [siteLists, setSiteLists] = useState<GatewaySiteProps[]>([]);

  const [selectedStatus, setSelectedStatus] = useState<string[]>(
    deviceListFilter.filterItems.status
  );
  const [selectedDeviceTypes, setSelectedDeviceTypes] = useState<string[]>(
    deviceListFilter.filterItems.gatewaytype
  );
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>(
    deviceListFilter.filterItems.servesto
  );
  const [selectedSites, setSelectedSites] = useState<string[]>(
    deviceListFilter.filterItems.sites
  );

  const [isFilterInitialized, setIsFilterInitialized] = useState(false);


  const {
    data: filterMenuData,
    isLoading: filterMenuLoading,
    isSuccess: filterMenuSuccess
  } = useGetFilterMenuQuery(
    { organizationId: selectedOrg },
    {
      skip: !selectedOrg
    }
  );

  useEffect(() => {
    setIsFilterInitialized(areInitiallyFiltersSelected());
  }, [deviceListFilter.filterItems]);

  useEffect(() => {
    if (filterMenuSuccess) {
      console.log("filterMenuData", filterMenuData);
      setGetFilterMenuData(filterMenuData);
    }
  }, [filterMenuData]);

  const statusOptions = [
    {
      name: t(LOCALE_CONST.pending),
      id: GatewayStatus.Pending
    },
    {
      name: t(LOCALE_CONST.active),
      id: GatewayStatus.Active
    },
    {
      name: t(LOCALE_CONST.error),
      id: GatewayStatus.Error
    },
    {
      name: t(LOCALE_CONST.deactivated),
      id: GatewayStatus.Deactivated
    },
    {
      name: t(LOCALE_CONST.certificateExpired),
      id: GatewayStatus.CertificateExpired
    }
  ];

  const normalizeSpaces = (str: string) => str.toLowerCase().replace(/\s+/g, ' ').trim();
  const organizationsFilteredColumns = filterMenuData?.organization?.filter((header: any) => 
    normalizeSpaces(header.organizationName).includes(normalizeSpaces(searchQuery))
  );  

  // For Site Customer Type
  const {
    data: siteDataLists
  } = useGetOrgSiteListsQuery(
    { orgId: customerOrgId },
    {
      skip: !customerType
    }
  );

  useEffect(() => {
    if (siteDataLists?.resources?.length > 0) {
      const siteValLists: ISiteListProps[] = [];
      siteDataLists?.resources?.forEach((val: ISiteListProps) => {         
        siteValLists.push({
          name: val?.name || "",
          id: val?.id || "",
          metadata: val?.metadata || [],
          type: val?.type || "",
          modelId: val?.modelId || "",
          externalId: val?.externalId || ""
        });          
      });
      setSiteLists(
        [...siteValLists].sort((a, b) => a.name.localeCompare(b.name))
      );  
    }        
  }, [siteDataLists]);
  
  const filteredSiteLists = customerType
  ? siteLists
  : Array.isArray(filterMenuData?.site) && filterMenuData?.site?.length > 0
  ? [...filterMenuData.site].sort((a: any, b: any) => a.name.localeCompare(b.name))
  : [];
  const sitesFilteredColumns = filteredSiteLists?.filter((siteVals: any) => 
    normalizeSpaces(siteVals.name).includes(normalizeSpaces(sitesSearchQuery))
  );

  const handleCheckboxChange = (
    option: string,
    selectedList: string[] | null,
    setSelectedList: (value: string[]) => void
  ) => {
    if (!selectedList) {
      setSelectedList([option]);
      return;
    }
  
    if (selectedList.includes(option)) {
      setSelectedList(selectedList.filter((item) => item !== option));
    } else {
      setSelectedList([...selectedList, option]);
    }
  };

  const handleApplyFilters = () => {
    const filters = {
      ...(selectedStatus?.length > 0 && { status: selectedStatus }),
      ...(selectedDeviceTypes?.length > 0 && {
        gatewaytype: selectedDeviceTypes
      }),
      ...(selectedOrganizations?.length > 0 && {
        servesto: selectedOrganizations
      }),
      ...(selectedSites?.length > 0 && { sites: selectedSites })
    };
    const formattedQueryString = Object.entries(filters)
      .filter(([, value]) => value !== null)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");
    dispatch({
      type: "SET_FILTER_ITEMS",
      payload: {
        deviceListFilter: {
          filterItems: filters,
          filterReqObj: (customerType && customerOrgId !== '') ? `servesto=${customerOrgId}&${formattedQueryString}` : formattedQueryString,
          isFilterApplied: true,
          isFilterOpen: false
        }
      }
    });
    // Dispatch initial pagination values after applying filters
    dispatch({
      type: "SET_DEVICE_LIST_PAGINATION",
      payload: {
        deviceListPagination: {
          page: 1,
          pageSize: 10
        }
      }
    });
  };

  const handleClearFilters = () => {    
    setSelectedStatus([]);
    setSelectedDeviceTypes([]);
    setSelectedOrganizations([]);
    setSelectedSites([]);
    dispatch({
				type: "SET_FILTER_ITEMS",
				payload: { 
          deviceListFilter: { 
            filterItems: [],  
            filterReqObj: '',
            isFilterApplied: false,
            isFilterOpen: false
        }}
    });
    // Dispatch initial pagination values after applying filters
    dispatch({
      type: "SET_DEVICE_LIST_PAGINATION",
      payload: {
        deviceListPagination: {
          page: 1,
          pageSize: 10
        }
      }
    });
  };

  const areFiltersSelected = () => {
    return (
      (selectedStatus?.length || 0) > 0 ||
      (selectedDeviceTypes?.length || 0) > 0 ||
      (selectedOrganizations?.length || 0) > 0 ||
      (selectedSites?.length || 0) > 0
    );
  };

  const areInitiallyFiltersSelected = () => {
    return (
      (deviceListFilter.filterItems.status?.length || 0) > 0 ||
      (deviceListFilter.filterItems.gatewaytype?.length || 0) > 0 ||
      (deviceListFilter.filterItems.servesto?.length || 0) > 0 ||
      (deviceListFilter.filterItems.sites?.length || 0) > 0
    );
  };

  const isApplyBtnDisabled = () => {
    if (isFilterInitialized) {
      return false;
    } else if (!areFiltersSelected()) {
      return true;
    } else {
      return false;
    }
  };

  // Modal close
  const handleModalClose = () => {
    dispatch({
      type: "SET_FILTER_ITEMS",
      payload: {
        deviceListFilter: {
          ...deviceListFilter,
          isFilterOpen: false
        }
      }
    });
  };

  const filterButtonRight = (
    <div className="flex gap-2">
      <Button
        variant="secondary"
        className="cancel-button"
        data-testid="cancel-button-test"
        onClick={handleModalClose}
      >
        {t(LOCALE_CONST.cancel)}
      </Button>
      <Button
      variant="primary"
      onClick={handleApplyFilters}
      disabled={isApplyBtnDisabled()}
    >
      {t(LOCALE_CONST.apply)}
    </Button>
    </div>
  );

  const filterButtonLeft = (
    <Button
      variant="quiet-primary"
      onClick={handleClearFilters}
      className="tracking-wider"
      disabled={!areFiltersSelected()}
    >
      {t(LOCALE_CONST.clearFilters)}
    </Button>
  );

  return (
    <Fragment>
      (
      <Modal
        data-testid="Modal"
        className="filter-modal-dialog"
        onModalClose={handleModalClose}
        title={t(LOCALE_CONST.filter)}
        footerRight={filterButtonRight}
        footerLeft={filterButtonLeft}
      >
        {filterMenuLoading && (
          <ContentLoader> {t(LOCALE_CONST.loadingDetails)}... </ContentLoader>
        )}
        {!filterMenuLoading && (
          <Accordion
            className={`filter-accordian ${customerType ? "hide-org-dropdown" : ""}`}
            defaultOpenIndex={[0]}
            id="sample"
          >
            <AccordionItem
              id="status"
              title={t(LOCALE_CONST.status)}
              titleCounter={selectedStatus?.length || 0}
            >
              {statusOptions.map((option) => (
                <div key={option.id} className="pb-4">
                  <Checkbox
                    id={option.id}
                    checked={selectedStatus?.includes(option.id)}
                    data-testid="checkbox-test"
                    onChange={() =>
                      handleCheckboxChange(
                        option.id,
                        selectedStatus,
                        setSelectedStatus
                      )
                    }
                  >
                    {option.name}
                  </Checkbox>
                </div>
              ))}
            </AccordionItem>
            <AccordionItem
              id="deviceType"
              title={t(LOCALE_CONST.gatewayType)}
              titleCounter={selectedDeviceTypes?.length || 0}
            >
              {filterMenuData?.gatewayType?.length === 0 && (
                <div>{t(LOCALE_CONST.noGatewayTypeFound)}</div>
              )}
              {filterMenuData?.gatewayType?.length > 0 &&
                filterMenuData?.gatewayType?.map((type: any) => (
                  <div key={type} className="pb-4">
                    <Checkbox
                      id={type}
                      checked={selectedDeviceTypes?.includes(type)}
                      data-testid="checkbox-test"
                      onChange={() =>
                        handleCheckboxChange(
                          type,
                          selectedDeviceTypes,
                          setSelectedDeviceTypes
                        )
                      }
                    >
                      {type}
                    </Checkbox>
                  </div>
                ))}
            </AccordionItem>
            <AccordionItem
              id="organization"
              title={t(LOCALE_CONST.organization)}
              titleCounter={selectedOrganizations?.length || 0}
            >
              <Input
                placeholder={t(LOCALE_CONST.searchOrg)}
                id="organizationSearch"
                prefix={<SearchIcon strokeWidth={2} />}
                data-testid="organization-search-test"
                onChange={(e) => {
                  if (validateInput(e.target.value)) {
                    setSearchQuery(e.target.value);
                  }
                }}
                className="mb-4"
                value={searchQuery}
              />
              {organizationsFilteredColumns?.map((org: any) => (
                <div key={org.organizationId} className="pb-3">
                  <Checkbox
                    id={org.organizationId}
                    checked={selectedOrganizations?.includes(
                      org.organizationId
                    )}
                    data-testid="checkbox-test"
                    onChange={() =>
                      handleCheckboxChange(
                        org.organizationId,
                        selectedOrganizations,
                        setSelectedOrganizations
                      )
                    }
                  >
                    {org.organizationName}
                  </Checkbox>
                </div>
              ))}
              {organizationsFilteredColumns?.length === 0 && (
                <div>{t(LOCALE_CONST.noOrgFound)}</div>
              )}
            </AccordionItem>
            <AccordionItem
              id="site"
              title={t(LOCALE_CONST.site)}
              titleCounter={selectedSites?.length || 0}
            >
              <Input
                placeholder={t(LOCALE_CONST.searchSites)}
                id="SitesSearch"
                prefix={<SearchIcon strokeWidth={2} />}
                data-testid="sites-search-test"
                onChange={(e) => {
                  if (validateInput(e.target.value)) {
                    setSitesSearchQuery(e.target.value);
                  }
                }}
                className="mb-4"
                value={sitesSearchQuery}
              />
              {sitesFilteredColumns?.map((site: any) => (
                <div key={site.id} className="pb-3">
                  <Checkbox
                    id={site.id}
                    checked={selectedSites?.includes(site.id)}
                    data-testid="checkbox-test"
                    onChange={() =>
                      handleCheckboxChange(
                        site.id,
                        selectedSites,
                        setSelectedSites
                      )
                    }
                  >
                    {site.name}
                  </Checkbox>
                </div>
              ))}
              {sitesFilteredColumns?.length === 0 && <div>{t(LOCALE_CONST.noSitesFound)}</div>}
            </AccordionItem>
          </Accordion>
        )}
      </Modal>
      )
    </Fragment>
  );
};

export default FilterModal;
